/**
 * Sequence component for Steve Render Service.
 *
 * Time-shifts its children so they appear/disappear at specific frames.
 * Supports premounting (rendering early but hidden) so video elements
 * can buffer before becoming visible.
 *
 * Written from scratch for Steve.AI.
 */

import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AbsoluteFill } from './AbsoluteFill.js';
import { CompositionManager } from './composition-context.js';
import { useRemotionEnvironment } from './get-environment.js';
import { useNonce } from './nonce.js';
import { SequenceContext } from './SequenceContext.js';
import { Freeze } from './fireworks/freeze.js';
import { TimelineContext, useTimelinePosition } from './timeline-position-state.js';
import { useVideoConfig } from './use-video-config.js';

/**
 * Core sequence logic — determines visibility and provides offset context.
 */
const RegularSequenceRefForwardingFunction = (
  {
    from = 0,
    durationInFrames = Infinity,
    children,
    name,
    showInTimeline = true,
    loopDisplay,
    _remotionInternalIsPremounting,
    ...other
  },
  ref
) => {
  const { layout = 'absolute-fill' } = other;
  const [id] = useState(() => String(Math.random()));
  const parentSequence = useContext(SequenceContext);
  const { rootId } = useContext(TimelineContext);
  const nonce = useNonce();
  const environment = useRemotionEnvironment();

  const cumulatedFrom = parentSequence
    ? parentSequence.cumulatedFrom + parentSequence.relativeFrom
    : 0;

  if (layout !== 'absolute-fill' && layout !== 'none') {
    throw new TypeError(
      `<Sequence> layout must be "absolute-fill" or "none", got: ${layout}`
    );
  }

  const absoluteFrame = useTimelinePosition();
  const videoConfig = useVideoConfig();

  const parentSequenceDuration = parentSequence
    ? Math.min(parentSequence.durationInFrames - from, durationInFrames)
    : durationInFrames;

  // Use the passed durationInFrames directly.
  // Do NOT clamp against (videoConfig.durationInFrames - from) — that formula
  // treats `from` as relative to the composition start, but at the top level
  // `from` is an absolute frame offset. Clamping caused every scene to inherit
  // the full remaining project duration, making scene 0 visible for the entire
  // video and all scenes overlapping.
  const actualDurationInFrames = Math.max(0, parentSequenceDuration);

  const { registerSequence, unregisterSequence } = useContext(CompositionManager);

  const contextValue = useMemo(() => ({
    cumulatedFrom,
    relativeFrom: from,
    durationInFrames: actualDurationInFrames,
    parentFrom: parentSequence?.relativeFrom ?? 0,
    id,
  }), [cumulatedFrom, from, actualDurationInFrames, parentSequence?.relativeFrom, id]);

  // Register with composition manager in preview mode
  useEffect(() => {
    if (environment !== 'preview') return;
    registerSequence({
      from,
      duration: actualDurationInFrames,
      id,
      displayName: name || `Sequence`,
      parent: parentSequence?.id ?? null,
      type: 'sequence',
      rootId,
      showInTimeline,
      nonce,
      loopDisplay,
    });
    return () => unregisterSequence(id);
  }, [
    durationInFrames, id, name, registerSequence, unregisterSequence,
    parentSequence?.id, actualDurationInFrames, rootId, from,
    showInTimeline, nonce, loopDisplay, environment,
  ]);

  // Determine visibility based on frame position
  const startThreshold = cumulatedFrom + from;
  const endThreshold = startThreshold + durationInFrames;

  // Use strict < for end so scene N ends exactly when scene N+1 starts.
  // Previously used <= (endThreshold - 1) which caused a 1-frame overlap at boundaries.
  const isVisible = absoluteFrame >= startThreshold && absoluteFrame < endThreshold;
  const content = _remotionInternalIsPremounting ? children : (isVisible ? children : null);

  const styleIfThere = layout === 'none' ? undefined : other.style;
  const defaultStyle = useMemo(() => ({
    flexDirection: undefined,
    ...(styleIfThere ?? {}),
  }), [styleIfThere]);

  return (
    <SequenceContext.Provider value={contextValue}>
      {content === null ? null : layout === 'none' ? (
        content
      ) : (
        <AbsoluteFill ref={ref} style={defaultStyle} className={other.className}>
          {content}
        </AbsoluteFill>
      )}
    </SequenceContext.Provider>
  );
};

const RegularSequence = forwardRef(RegularSequenceRefForwardingFunction);

/**
 * Premounted sequence — renders early (frozen + hidden) for buffering.
 */
const PremountedSequenceRefForwardingFunction = (props, ref) => {
  const {
    style: passedStyle,
    from = 0,
    durationInFrames = Infinity,
    premountFor = 0,
    ...otherProps
  } = props;

  const absoluteFrame = useTimelinePosition();
  const parentSequence = useContext(SequenceContext);
  const cumulatedFrom = parentSequence
    ? parentSequence.cumulatedFrom + parentSequence.relativeFrom
    : 0;

  const startThreshold = cumulatedFrom + from;
  const premountingActive = absoluteFrame >= startThreshold - premountFor && absoluteFrame < startThreshold;

  const style = useMemo(() => ({
    ...(passedStyle || {}),
    ...(premountingActive ? { opacity: 0, pointerEvents: 'none' } : {}),
  }), [passedStyle, premountingActive]);

  return (
    <Freeze frame={from} active={premountingActive}>
      <RegularSequence
        ref={ref}
        from={from}
        durationInFrames={durationInFrames}
        style={style}
        _remotionInternalIsPremounting={premountingActive}
        {...otherProps}
      />
    </Freeze>
  );
};

const PremountedSequence = forwardRef(PremountedSequenceRefForwardingFunction);

/**
 * Main Sequence export — auto-selects premounted version when needed.
 */
const SequenceRefForwardingFunction = (props, ref) => {
  const env = useRemotionEnvironment();

  if (props.layout !== 'none' && props.premountFor && env !== 'rendering') {
    return <PremountedSequence ref={ref} {...props} />;
  }

  const { premountFor, ...rest } = props;
  return <RegularSequence ref={ref} {...rest} />;
};

export const Sequence = forwardRef(SequenceRefForwardingFunction);
